import { BrowserModule } from '@angular/platform-browser';

import { APP_INITIALIZER, NgModule, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { OktaAuthModule, OKTA_CONFIG } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { DragulaModule } from 'ng2-dragula';
import moment from 'moment-timezone';
import {
  ApiModule,
  ErrorHandlerService,
  GoogleAnalyticsService,
  GraphQLModule,
  RouteEventsStorageService,
  SubdomainInterceptorService,
  TokenInterceptorService,
  WindowRef,
} from '@aptitude/shared';

import { AppRoutingModule } from './app-routing.module';
import { LayoutModule } from './layout/layout.module';

import { AppComponent } from './app.component';

import { environment } from '../environments/environment';
import { UtilModule } from './util/util.module';
import { ExternalHomeComponent } from './external-home/external-home.component';
import {
  RECAPTCHA_V3_SITE_KEY,
  RecaptchaFormsModule,
  RecaptchaModule,
  RecaptchaV3Module,
} from 'ng-recaptcha';
import { EnvironmentService } from '@aptitude/environment';
import { take } from 'rxjs/operators';

const oktaAuth = new OktaAuth({
  issuer: environment.oktaIssuer,
  clientId: environment.oktaClientId,
  redirectUri: `${window.location.origin}/aptitude/static/platform/signIn/login/callback`,
});

/**
 * This AppModule is the main module for the Angular version of the platform
 *
 * Don't import components here, instead import the module that contains them
 * Every module should declare its own components and export the ones that should be visible here
 *
 */
@NgModule({
  declarations: [AppComponent, ExternalHomeComponent],
  imports: [
    ApiModule,
    AppRoutingModule,
    BrowserModule,
    HttpClientModule,
    LayoutModule,
    UtilModule,
    RecaptchaModule,
    RecaptchaFormsModule,
    RecaptchaV3Module,

    // Drag and Drop
    DragulaModule.forRoot(),

    GraphQLModule,
    OktaAuthModule,
  ],
  providers: [
    AppComponent,
    WindowRef,
    { provide: OKTA_CONFIG, useValue: { oktaAuth } },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptorService,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorHandlerService, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SubdomainInterceptorService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [RouteEventsStorageService],
      multi: true,
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useFactory: (environmentService: EnvironmentService) => {
        let recaptchaKey = '';

        environmentService.environmentData$
          .pipe(take(1))
          .subscribe((env) => (recaptchaKey = env.recaptchaKey));

        return recaptchaKey;
      },
      deps: [EnvironmentService],
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    public googleAnalyticsService: GoogleAnalyticsService,
    private router: Router,
    private injector: Injector,
  ) {
    googleAnalyticsService.loadGoogleAnalytics(
      environment.googleAnalytics.id,
      environment.googleAnalytics.tag,
    );
    googleAnalyticsService.startTracking();

    // I'm just going to say that this is straight up horrifying. Our handling of time/date is poor.
    moment.tz.setDefault('America/Chicago');
  }

  // Explicit call for angular to handle bootstrap for Angular elements
  ngDoBootstrap() {}
}
